import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, ArrowRight, BookOpen, LifeBuoy, Loader2, ShieldAlert } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  BUSINESS_TEACHING,
  CRYPTO_NO_WALLET_STOP,
  CRYPTO_TEACHING,
  EQUIPMENT_VALUE_NOTE,
  LIFE_INSURANCE_TEACHING,
  METALS_CUSTODY_NOTE,
  MONEY_ACCOUNTS_LINE,
  HOME_LIVED_IN_TEACHING,
  MISSION_PROPERTY_TEACHING,
  MONEY_TEACHING,
  ORIENTATION_DO,
  ORIENTATION_KNOW,
  ORIENTATION_SCHEDULE,
  RENTAL_TEACHING,
  SECURITIES_CLOSING_LINE,
  SECURITIES_NO_ACCOUNT_STOP,
  SECURITIES_TEACHING_ONE,
  SECURITIES_TEACHING_TWO,
  SEQUENCING_TEACHING,
  STORM_OPTIONS,
  STORM_QUESTION,
  STORM_STOP_NOT_SURE,
  STORM_STOP_YES,
  THING_OPTIONS,
  VEHICLE_FAMILY_PLAIN,
  VEHICLE_JOB_OPTIONS,
  VEHICLE_NOT_SURE_FOLLOWUP,
  VEHICLE_NOT_SURE_HELPER,
  VEHICLE_PRIVACY_STOP,
  routeGiving,
  type GivingWorkflowData,
  type LandUse,
  type StormAnswer,
  type ThingKind,
  type VehicleJob,
} from './givingContent';

interface GivingPhase1Props {
  task: any;
  phaseData?: any;
  onPhaseComplete: () => void;
}

const STEP_TITLES = ['Start here', 'One question first', 'What is the thing?', 'About the thing', 'Review'];

function AskAPerson() {
  return (
    <Button asChild variant="outline" size="sm" className="min-h-11">
      <a href="/support/ask">
        <LifeBuoy className="h-4 w-4 mr-2" />
        Ask a person
      </a>
    </Button>
  );
}

export function GivingPhase1({ task, onPhaseComplete }: GivingPhase1Props) {
  const { toast } = useToast();
  const existing: GivingWorkflowData = task?.workflow_data?.giving || {};

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [unaName, setUnaName] = useState('');

  const [storm, setStorm] = useState<StormAnswer | undefined>(existing.storm);
  const [thing, setThing] = useState<ThingKind | undefined>(existing.thing);

  const [vehicleJob, setVehicleJob] = useState<VehicleJob | undefined>(existing.vehicle_job);
  const [vehiclePrivacy, setVehiclePrivacy] = useState(!!existing.vehicle_privacy_pattern);
  const [vehicleDescription, setVehicleDescription] = useState(existing.vehicle_description || '');
  const [vehicleVin, setVehicleVin] = useState(existing.vehicle_vin || '');
  const [vehicleDriver, setVehicleDriver] = useState(existing.vehicle_driver || '');

  const [landLivedIn, setLandLivedIn] = useState<'yes' | 'no' | undefined>(existing.land_lived_in);
  const [landUse, setLandUse] = useState<LandUse | undefined>(existing.land_use);
  const [landLlcExists, setLandLlcExists] = useState(!!existing.land_llc_exists);
  const [propertyAddress, setPropertyAddress] = useState(existing.property_address || '');
  const [propertyCounty, setPropertyCounty] = useState(existing.property_county || '');

  const [securitiesDescription, setSecuritiesDescription] = useState(existing.securities_description || '');
  const [securitiesInstitution, setSecuritiesInstitution] = useState(existing.securities_institution || '');
  const [securitiesAccount, setSecuritiesAccount] = useState<'yes' | 'no' | undefined>(
    existing.securities_entity_account_exists,
  );

  const [cryptoDescription, setCryptoDescription] = useState(existing.crypto_description || '');
  const [cryptoWallet, setCryptoWallet] = useState<'yes' | 'no' | undefined>(existing.crypto_entity_wallet_exists);

  const [equipmentDescription, setEquipmentDescription] = useState(existing.equipment_description || '');
  const [equipmentValue, setEquipmentValue] = useState(existing.equipment_value || '');
  const [equipmentCustody, setEquipmentCustody] = useState(existing.equipment_custody || '');

  const [giverName, setGiverName] = useState(existing.giver_name || '');
  const [receivingEntity, setReceivingEntity] = useState(existing.receiving_entity || '');
  const [spousalConsent, setSpousalConsent] = useState(!!existing.spousal_consent_needed);
  const [notes, setNotes] = useState(existing.notes || '');

  useEffect(() => {
    async function loadUna() {
      if (!task?.una_id) return;
      const { data } = await supabase.from('unas').select('name').eq('id', task.una_id).maybeSingle();
      if (data?.name) {
        setUnaName(data.name);
        setReceivingEntity((prev) => prev || data.name);
      }
    }
    loadUna();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.una_id]);

  const data: GivingWorkflowData = useMemo(
    () => ({
      storm,
      thing,
      vehicle_job: vehicleJob,
      vehicle_privacy_pattern: vehiclePrivacy,
      vehicle_description: vehicleDescription,
      vehicle_vin: vehicleVin,
      vehicle_driver: vehicleDriver,
      land_lived_in: landLivedIn,
      land_use: landUse,
      land_llc_exists: landLlcExists,
      property_address: propertyAddress,
      property_county: propertyCounty,
      securities_description: securitiesDescription,
      securities_institution: securitiesInstitution,
      securities_entity_account_exists: securitiesAccount,
      crypto_description: cryptoDescription,
      crypto_entity_wallet_exists: cryptoWallet,
      equipment_description: equipmentDescription,
      equipment_value: equipmentValue,
      equipment_custody: equipmentCustody,
      giver_name: giverName,
      receiving_entity: receivingEntity,
      spousal_consent_needed: spousalConsent,
      notes,
    }),
    [
      storm,
      thing,
      vehicleJob,
      vehiclePrivacy,
      vehicleDescription,
      vehicleVin,
      vehicleDriver,
      landLivedIn,
      landUse,
      landLlcExists,
      propertyAddress,
      propertyCounty,
      securitiesDescription,
      securitiesInstitution,
      securitiesAccount,
      cryptoDescription,
      cryptoWallet,
      equipmentDescription,
      equipmentValue,
      equipmentCustody,
      giverName,
      receivingEntity,
      spousalConsent,
      notes,
    ],
  );

  const route = routeGiving(data);
  const stormBlocked = storm === 'yes' || storm === 'not_sure';
  const privacyBlocked = thing === 'vehicle' && vehiclePrivacy;

  async function persist() {
    setSaving(true);
    try {
      const nextData = { ...(task.workflow_data || {}), giving: data };
      await supabase.from('task_workflows').update({ workflow_data: nextData }).eq('task_id', task.id);
      if (task.workflow_data) task.workflow_data.giving = data;
    } catch (e: any) {
      console.error('Could not save the giving answers', e);
      toast({
        title: 'We could not save that',
        description: e.message || 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  function canContinue(): boolean {
    if (step === 2) return storm === 'no';
    if (step === 3) return !!thing;
    if (step === 4) {
      if (stormBlocked || privacyBlocked) return false;
      if (thing === 'vehicle') return !!vehicleJob && vehicleJob !== 'not_sure';
      if (thing === 'home_land') return landLivedIn === 'no' ? !!landUse : landLivedIn === 'yes';
      if (thing === 'securities') return !!securitiesAccount;
      if (thing === 'crypto') return !!cryptoWallet;
      return true;
    }
    return true;
  }

  async function next() {
    await persist();
    setStep((s) => Math.min(5, s + 1));
  }

  async function finish() {
    await persist();
    toast({ title: 'Saved', description: 'Your answers are saved. Next you prepare the papers.' });
    onPhaseComplete();
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-base">
              Step {step} of 5: {STEP_TITLES[step - 1]}
            </CardTitle>
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <div className="space-y-4 text-sm leading-relaxed">
              <div>
                <h4 className="font-medium mb-1">What you need to know</h4>
                <p>{ORIENTATION_KNOW}</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">What you need to do</h4>
                <p>{ORIENTATION_DO}</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">Your schedule</h4>
                <p>{ORIENTATION_SCHEDULE}</p>
              </div>
              <Button asChild variant="outline" size="sm" className="min-h-11">
                <a href="/support/library">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Read the giving guides in the library
                </a>
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm">{STORM_QUESTION}</p>
              <RadioGroup value={storm} onValueChange={(v) => setStorm(v as StormAnswer)} className="space-y-3">
                {STORM_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value={opt.value} id={`storm-${opt.value}`} className="mt-1" />
                    <Label htmlFor={`storm-${opt.value}`} className="font-normal cursor-pointer">
                      <span className="font-medium block">{opt.label}</span>
                      <span className="text-sm text-muted-foreground">{opt.helper}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {stormBlocked && (
                <Alert className="border-destructive/50">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertDescription className="space-y-3 text-sm">
                    <p>{storm === 'yes' ? STORM_STOP_YES : STORM_STOP_NOT_SURE}</p>
                    <p>No papers prepare while this is the answer.</p>
                    <AskAPerson />
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">What is the thing?</p>
              <RadioGroup value={thing} onValueChange={(v) => setThing(v as ThingKind)} className="space-y-3">
                {THING_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value={opt.value} id={`thing-${opt.value}`} className="mt-1" />
                    <Label htmlFor={`thing-${opt.value}`} className="font-normal cursor-pointer">
                      <span className="font-medium block">{opt.label}</span>
                      <span className="text-sm text-muted-foreground">{opt.helper}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {step === 4 && thing === 'vehicle' && (
            <div className="space-y-5">
              <div className="space-y-3">
                <Label>Whose job is this vehicle?</Label>
                <RadioGroup
                  value={vehicleJob}
                  onValueChange={(v) => setVehicleJob(v as VehicleJob)}
                  className="space-y-3"
                >
                  {VEHICLE_JOB_OPTIONS.map((opt) => (
                    <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3">
                      <RadioGroupItem value={opt.value} id={`vjob-${opt.value}`} className="mt-1" />
                      <Label htmlFor={`vjob-${opt.value}`} className="font-normal cursor-pointer">
                        <span className="font-medium block">{opt.label}</span>
                        <span className="text-sm text-muted-foreground">{opt.helper}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                {vehicleJob === 'not_sure' && (
                  <Alert>
                    <AlertDescription className="space-y-2 text-sm">
                      <p>{VEHICLE_NOT_SURE_HELPER}</p>
                      <p>{VEHICLE_NOT_SURE_FOLLOWUP}</p>
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <div className="flex items-start gap-3 rounded-lg border p-3">
                <Checkbox
                  id="privacy-pattern"
                  checked={vehiclePrivacy}
                  onCheckedChange={(c) => setVehiclePrivacy(!!c)}
                  className="mt-1"
                />
                <Label htmlFor="privacy-pattern" className="font-normal cursor-pointer">
                  This vehicle mostly protects our privacy rather than serving operations
                </Label>
              </div>

              {vehiclePrivacy && (
                <Alert className="border-amber-500/50">
                  <AlertDescription className="space-y-3 text-sm">
                    <p>{VEHICLE_PRIVACY_STOP}</p>
                    <AskAPerson />
                  </AlertDescription>
                </Alert>
              )}

              {!vehiclePrivacy && vehicleJob === 'family' && (
                <Alert>
                  <AlertDescription className="text-sm">{VEHICLE_FAMILY_PLAIN}</AlertDescription>
                </Alert>
              )}

              {!vehiclePrivacy && (vehicleJob === 'family' || vehicleJob === 'mission') && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="v-desc">The vehicle</Label>
                    <Input
                      id="v-desc"
                      value={vehicleDescription}
                      onChange={(e) => setVehicleDescription(e.target.value)}
                      placeholder="Year, make, and model"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="v-vin">Vehicle identification number</Label>
                    <Input id="v-vin" value={vehicleVin} onChange={(e) => setVehicleVin(e.target.value)} />
                  </div>
                  {vehicleJob === 'mission' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="v-entity">Which entity receives it</Label>
                        <Input
                          id="v-entity"
                          value={receivingEntity}
                          onChange={(e) => setReceivingEntity(e.target.value)}
                          placeholder={unaName || 'Exact legal name'}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="v-driver">Who will drive it</Label>
                        <Input
                          id="v-driver"
                          value={vehicleDriver}
                          onChange={(e) => setVehicleDriver(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 4 && thing === 'home_land' && (
            <div className="space-y-5">
              <div className="space-y-3">
                <Label>Does your household live there, or plan to?</Label>
                <RadioGroup
                  value={landLivedIn}
                  onValueChange={(v) => setLandLivedIn(v as 'yes' | 'no')}
                  className="space-y-3"
                >
                  <div className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value="yes" id="lived-yes" className="mt-1" />
                    <Label htmlFor="lived-yes" className="font-normal cursor-pointer">
                      Yes
                    </Label>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value="no" id="lived-no" className="mt-1" />
                    <Label htmlFor="lived-no" className="font-normal cursor-pointer">
                      No
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {landLivedIn === 'yes' && (
                <Alert>
                  <AlertDescription className="space-y-3 text-sm">
                    <p>{HOME_LIVED_IN_TEACHING}</p>
                    <p>No papers prepare here.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild variant="outline" size="sm" className="min-h-11">
                        <a href="/workflows">Open the center workflow</a>
                      </Button>
                      <AskAPerson />
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {landLivedIn === 'no' && (
                <div className="space-y-4">
                  <Label>Is it a rental with tenants, or land and buildings for the mission's use?</Label>
                  <RadioGroup value={landUse} onValueChange={(v) => setLandUse(v as LandUse)} className="space-y-3">
                    <div className="flex items-start gap-3 rounded-lg border p-3">
                      <RadioGroupItem value="rental" id="use-rental" className="mt-1" />
                      <Label htmlFor="use-rental" className="font-normal cursor-pointer">
                        A rental with tenants
                      </Label>
                    </div>
                    <div className="flex items-start gap-3 rounded-lg border p-3">
                      <RadioGroupItem value="mission" id="use-mission" className="mt-1" />
                      <Label htmlFor="use-mission" className="font-normal cursor-pointer">
                        Land and buildings for the mission's use
                      </Label>
                    </div>
                  </RadioGroup>

                  {landUse === 'rental' && (
                    <Alert>
                      <AlertDescription className="space-y-3 text-sm">
                        <p>{RENTAL_TEACHING}</p>
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="llc-exists"
                            checked={landLlcExists}
                            onCheckedChange={(c) => setLandLlcExists(!!c)}
                            className="mt-1"
                          />
                          <Label htmlFor="llc-exists" className="font-normal cursor-pointer">
                            The property state company already exists
                          </Label>
                        </div>
                        {!landLlcExists && (
                          <Button asChild variant="outline" size="sm" className="min-h-11">
                            <a href="/workflows">Form the company first</a>
                          </Button>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  {landUse === 'mission' && (
                    <Alert>
                      <AlertDescription className="text-sm">{MISSION_PROPERTY_TEACHING}</AlertDescription>
                    </Alert>
                  )}

                  {(landUse === 'mission' || (landUse === 'rental' && landLlcExists)) && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="p-address">Property address</Label>
                        <Input
                          id="p-address"
                          value={propertyAddress}
                          onChange={(e) => setPropertyAddress(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="p-county">County</Label>
                        <Input
                          id="p-county"
                          value={propertyCounty}
                          onChange={(e) => setPropertyCounty(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="p-entity">Which entity receives it</Label>
                        <Input
                          id="p-entity"
                          value={receivingEntity}
                          onChange={(e) => setReceivingEntity(e.target.value)}
                          placeholder="Exact legal name"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 4 && thing === 'securities' && (
            <div className="space-y-5">
              <Alert>
                <AlertDescription className="space-y-2 text-sm">
                  <p>{SECURITIES_TEACHING_ONE}</p>
                  <p>{SECURITIES_TEACHING_TWO}</p>
                </AlertDescription>
              </Alert>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="s-desc">What is being given</Label>
                  <Textarea
                    id="s-desc"
                    value={securitiesDescription}
                    onChange={(e) => setSecuritiesDescription(e.target.value)}
                    rows={3}
                    placeholder="Shares, funds, or bonds, and how many"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="s-inst">Which institution holds it</Label>
                  <Input
                    id="s-inst"
                    value={securitiesInstitution}
                    onChange={(e) => setSecuritiesInstitution(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="s-entity">Which entity receives it</Label>
                  <Input
                    id="s-entity"
                    value={receivingEntity}
                    onChange={(e) => setReceivingEntity(e.target.value)}
                    placeholder={unaName || 'Exact legal name'}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Does the receiving entity already have an investment account?</Label>
                <RadioGroup
                  value={securitiesAccount}
                  onValueChange={(v) => setSecuritiesAccount(v as 'yes' | 'no')}
                  className="space-y-3"
                >
                  <div className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value="yes" id="s-acct-yes" className="mt-1" />
                    <Label htmlFor="s-acct-yes" className="font-normal cursor-pointer">
                      Yes, it is open
                    </Label>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value="no" id="s-acct-no" className="mt-1" />
                    <Label htmlFor="s-acct-no" className="font-normal cursor-pointer">
                      No, not yet
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {securitiesAccount === 'no' && (
                <Alert className="border-amber-500/50">
                  <AlertDescription className="space-y-3 text-sm">
                    <p>{SECURITIES_NO_ACCOUNT_STOP}</p>
                    <p>No papers prepare here.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild variant="outline" size="sm" className="min-h-11">
                        <a href="/support/library">
                          <BookOpen className="h-4 w-4 mr-2" />
                          Read the accounts guidance
                        </a>
                      </Button>
                      <AskAPerson />
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <p className="text-sm text-muted-foreground">{SECURITIES_CLOSING_LINE}</p>
            </div>
          )}

          {step === 4 && thing === 'crypto' && (
            <div className="space-y-5">
              <Alert>
                <AlertDescription className="text-sm">{CRYPTO_TEACHING}</AlertDescription>
              </Alert>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="c-desc">What coins, and roughly how much</Label>
                  <Textarea
                    id="c-desc"
                    value={cryptoDescription}
                    onChange={(e) => setCryptoDescription(e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-entity">Which entity receives it</Label>
                  <Input
                    id="c-entity"
                    value={receivingEntity}
                    onChange={(e) => setReceivingEntity(e.target.value)}
                    placeholder={unaName || 'Exact legal name'}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Does the entity have its own wallet, with its own seed?</Label>
                <RadioGroup
                  value={cryptoWallet}
                  onValueChange={(v) => setCryptoWallet(v as 'yes' | 'no')}
                  className="space-y-3"
                >
                  <div className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value="yes" id="c-wallet-yes" className="mt-1" />
                    <Label htmlFor="c-wallet-yes" className="font-normal cursor-pointer">
                      Yes, it is set up
                    </Label>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value="no" id="c-wallet-no" className="mt-1" />
                    <Label htmlFor="c-wallet-no" className="font-normal cursor-pointer">
                      No, not yet
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {cryptoWallet === 'no' && (
                <Alert className="border-amber-500/50">
                  <AlertDescription className="space-y-3 text-sm">
                    <p>{CRYPTO_NO_WALLET_STOP}</p>
                    <p>No papers prepare here.</p>
                    <AskAPerson />
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 4 && thing === 'life_insurance' && (
            <Alert>
              <AlertDescription className="space-y-3 text-sm">
                <p>{LIFE_INSURANCE_TEACHING}</p>
                <p>No papers prepare here.</p>
                <AskAPerson />
              </AlertDescription>
            </Alert>
          )}

          {step === 4 && thing === 'money' && (
            <Alert>
              <AlertDescription className="space-y-3 text-sm">
                <p>{MONEY_TEACHING}</p>
                <p>{MONEY_ACCOUNTS_LINE}</p>
                <p>No papers prepare here.</p>
                <Button asChild variant="outline" size="sm" className="min-h-11">
                  <a href="/workflows">Open Record a Contribution</a>
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {step === 4 && thing === 'business' && (
            <div className="space-y-4">
              <Alert>
                <AlertDescription className="space-y-3 text-sm">
                  <p>{BUSINESS_TEACHING}</p>
                  <p>One paper prepares here: the encumbrance review worksheet. Bring it filled in.</p>
                  <AskAPerson />
                </AlertDescription>
              </Alert>
              <div className="space-y-2">
                <Label htmlFor="b-giver">Who owns the company today</Label>
                <Input id="b-giver" value={giverName} onChange={(e) => setGiverName(e.target.value)} />
              </div>
            </div>
          )}

          {step === 4 && thing === 'equipment' && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="e-desc">What it is</Label>
                  <Textarea
                    id="e-desc"
                    value={equipmentDescription}
                    onChange={(e) => setEquipmentDescription(e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="e-value">Rough value</Label>
                  <Input id="e-value" value={equipmentValue} onChange={(e) => setEquipmentValue(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="e-entity">Which entity receives it</Label>
                  <Input
                    id="e-entity"
                    value={receivingEntity}
                    onChange={(e) => setReceivingEntity(e.target.value)}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="e-custody">Where will it be kept?</Label>
                  <Input
                    id="e-custody"
                    value={equipmentCustody}
                    onChange={(e) => setEquipmentCustody(e.target.value)}
                    placeholder="The place, and any storage or vault account or box"
                  />
                </div>
              </div>
              <Alert>
                <AlertDescription className="space-y-2 text-sm">
                  <p>{EQUIPMENT_VALUE_NOTE}</p>
                  <p>{METALS_CUSTODY_NOTE}</p>
                </AlertDescription>
              </Alert>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5 text-sm">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="giver">Who is giving</Label>
                  <Input id="giver" value={giverName} onChange={(e) => setGiverName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="receiver">Who is receiving</Label>
                  <Input
                    id="receiver"
                    value={receivingEntity}
                    onChange={(e) => setReceivingEntity(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border p-3">
                <Checkbox
                  id="spousal"
                  checked={spousalConsent}
                  onCheckedChange={(c) => setSpousalConsent(!!c)}
                  className="mt-1"
                />
                <Label htmlFor="spousal" className="font-normal cursor-pointer">
                  This is marital property, so add the spousal consent line
                </Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Anything the record should say</Label>
                <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
              </div>

              <Alert>
                <AlertDescription className="space-y-2 text-sm">
                  <p>{route.summary}</p>
                  <p>{SEQUENCING_TEACHING}</p>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <Button
              variant="outline"
              className="min-h-11"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {step < 5 ? (
              <Button onClick={next} disabled={!canContinue() || saving} className="min-h-11">
                Continue
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={finish} disabled={saving || route.hardStop} className="min-h-11">
                Save and go to the papers
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default GivingPhase1;
